import React, { useState } from "react";
import { FiCalendar, FiChevronDown } from "react-icons/fi";

type Props = {
  // Called when the user clicks Apply with their chosen range.
  // For a single day, from and to will be the same date.
  onApply: (from: string, to: string) => void;
  // The currently active label to show on the button (e.g. "2026-06-15").
  buttonLabel: string;
  // Earliest date that has data (yyyy-mm-dd) — used as a sensible default.
  defaultFrom?: string;
  defaultTo?: string;
};

export function DatePeriodPicker({ onApply, buttonLabel, defaultFrom, defaultTo }: Props) {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(defaultFrom || "");
  const [to, setTo] = useState(defaultTo || "");

  const apply = () => {
    if (!from) return;
    // If no "to" given, treat it as a single day.
    const end = to || from;
    // Guard against reversed range.
    const [a, b] = from <= end ? [from, end] : [end, from];
    onApply(a, b);
    setOpen(false);
  };

  const fieldStyle: React.CSSProperties = {
    width: "100%",
    marginTop: 5,
    padding: "8px 11px",
    borderRadius: 8,
    border: "1px solid var(--ct-line)",
    background: "var(--ct-surface)",
    color: "var(--ct-ink)",
    fontFamily: "inherit",
    fontSize: 13.5,
    outline: "none",
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 12.5,
    fontWeight: 500,
    color: "var(--ct-ink-2)",
  };

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      {/* The date range is a filter, not a headline: it sits at the same
          weight as the buttons beside it rather than glowing. */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontFamily: "inherit",
          fontSize: 13,
          fontWeight: 500,
          padding: "7px 13px",
          borderRadius: 8,
          border: "1px solid var(--ct-line)",
          background: "var(--ct-surface)",
          color: "var(--ct-ink)",
          cursor: "pointer",
          transition: "border-color .12s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = "var(--ct-ink-3)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = "var(--ct-line)";
        }}
      >
        <FiCalendar size={14} style={{ color: "var(--ct-ink-3)" }} />
        {buttonLabel}
        <FiChevronDown size={14} style={{ color: "var(--ct-ink-3)" }} />
      </button>

      {open && (
        <>
          {/* click-away backdrop */}
          <div
            onClick={() => setOpen(false)}
            style={{ position: "fixed", inset: 0, zIndex: 40 }}
          />

          <div
            style={{
              position: "absolute",
              top: "calc(100% + 8px)",
              right: 0,
              zIndex: 50,
              width: 300,
              padding: 16,
              borderRadius: 10,
              background: "var(--ct-surface)",
              border: "1px solid var(--ct-line)",
              boxShadow: "0 12px 32px rgba(15,23,42,0.14)",
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                letterSpacing: "-0.01em",
                color: "var(--ct-ink)",
                marginBottom: 14,
              }}
            >
              Choose a period
            </div>

            <label style={labelStyle}>
              From
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                style={fieldStyle}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = "var(--ct-accent)";
                  e.currentTarget.style.boxShadow = "0 0 0 3px var(--ct-accent-bg)";
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = "var(--ct-line)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              />
            </label>

            <div style={{ height: 14 }} />

            <label style={labelStyle}>
              To{" "}
              <span style={{ color: "var(--ct-ink-3)", fontWeight: 400 }}>
                (leave blank for a single day)
              </span>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                style={fieldStyle}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = "var(--ct-accent)";
                  e.currentTarget.style.boxShadow = "0 0 0 3px var(--ct-accent-bg)";
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = "var(--ct-line)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              />
            </label>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                marginTop: 18,
              }}
            >
              <button
                type="button"
                onClick={() => setOpen(false)}
                style={{
                  fontFamily: "inherit",
                  fontSize: 13,
                  fontWeight: 500,
                  padding: "7px 13px",
                  borderRadius: 8,
                  border: "1px solid var(--ct-line)",
                  background: "var(--ct-surface)",
                  color: "var(--ct-ink-2)",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={apply}
                disabled={!from}
                style={{
                  fontFamily: "inherit",
                  fontSize: 13,
                  fontWeight: 600,
                  padding: "7px 15px",
                  borderRadius: 8,
                  border: "1px solid var(--ct-accent)",
                  background: from ? "var(--ct-accent)" : "var(--ct-accent-bg)",
                  color: from ? "#ffffff" : "var(--ct-accent)",
                  cursor: from ? "pointer" : "not-allowed",
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
